/**
 * Map digirasa-api meal-plan masters → specialty plan UI shape used by
 * SpecialtyPlansSection / SpecialtyPlanDetailModal / WeeklyMenuTable.
 */

import { expandToFullWeek, MEAL_SLOT_TYPES, sortMealsBySlotOrder, sortWeekdays } from './mealSlotTypes';

export const REGION_LABELS = {
  south: 'South Indian',
  north: 'North Indian',
  east: 'East Indian',
  west: 'West Indian',
};

/** Presentation-only overlays (icons/accents/taglines are not in admin tables yet). */
export const SPECIALTY_UI_OVERLAYS = {
  general: {
    tagline: 'South & North Indian homestyle meals — organic & non-organic',
    accent: '#7c3aed',
  },
  gym: {
    tagline: 'High-protein vegetarian meals for active lifestyles',
    accent: '#2563eb',
  },
  diabetic: {
    tagline: 'Low-GI meals tailored for blood sugar balance',
    accent: '#059669',
  },
  weightloss: {
    tagline: 'Calorie-conscious rotating weekly menus',
    accent: '#c45c26',
    organicOnly: true,
  },
};

const SINGLE_SLOT_BUNDLES = {
  Breakfast: 'breakfast',
  'Mid Mor Snack': 'mid_morning',
  Lunch: 'lunch',
  'Evng Snack': 'evening_snack',
  Dinner: 'dinner',
};

function slotCodeById(slots) {
  const map = {};
  (slots || []).forEach((slot) => {
    if (slot?.id != null) map[slot.id] = slot.code;
  });
  return map;
}

/**
 * Collapse API price rows (one per organic flag) into UI pricing rows
 * `{ bundle, perDayNonOrganic, perDayOrganic, exclGstNonOrganic, exclGstOrganic }`.
 */
export function groupPricesForSpecialty(prices, specialtyId) {
  const byBundle = new Map();
  (prices || [])
    .filter((row) => row.specialtyId === specialtyId && row.status !== 'hidden')
    .forEach((row) => {
      const bundle = row.bundleKey || (row.slotId != null ? `slot-${row.slotId}` : 'Package');
      if (!byBundle.has(bundle)) {
        byBundle.set(bundle, {
          bundle,
          slotId: row.slotId ?? null,
          perDayNonOrganic: null,
          perDayOrganic: null,
          exclGstNonOrganic: null,
          exclGstOrganic: null,
        });
      }
      const entry = byBundle.get(bundle);
      if (row.slotId != null) entry.slotId = row.slotId;
      if (row.isOrganic) {
        entry.perDayOrganic = Number(row.perDayPrice);
        entry.exclGstOrganic = row.exclGst != null ? Number(row.exclGst) : null;
      } else {
        entry.perDayNonOrganic = Number(row.perDayPrice);
        entry.exclGstNonOrganic = row.exclGst != null ? Number(row.exclGst) : null;
      }
    });

  const preferred = [
    'Complete Meal',
    'Complete weekly plan',
    'Breakfast',
    'Mid Mor Snack',
    'Bf N Mrng Snack',
    'Bf N Snack',
    'Lunch',
    'Lunch N Evng Snack',
    'Lunch N Snack',
    'Evng Snack',
    'Dinner',
  ];
  const rows = Array.from(byBundle.values());
  rows.sort((a, b) => {
    const ai = preferred.indexOf(a.bundle);
    const bi = preferred.indexOf(b.bundle);
    if (ai === -1 && bi === -1) return a.bundle.localeCompare(b.bundle);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return rows;
}

/** Prefer Complete Meal as card "from" price; else first priced row. */
export function pickFromPriceRow(pricing) {
  if (!pricing?.length) return null;
  return (
    pricing.find((row) => /complete/i.test(row.bundle || ''))
    || pricing.find((row) => row.perDayNonOrganic != null || row.perDayOrganic != null)
    || pricing[0]
  );
}

/**
 * Convert indicative API items into weekday menu rows.
 * @param {Array} items
 * @param {Array} slots — meal_plan_slot rows
 * @param {string|null} regionKey — filter; null keeps non-regional + matching
 */
export function indicativeItemsToWeeklyMenu(items, slots, regionKey = null) {
  const codeById = slotCodeById(slots);
  const filtered = (items || []).filter((item) => {
    if (item.status === 'hidden') return false;
    if (!regionKey) {
      return !item.regionKey;
    }
    return !item.regionKey || item.regionKey === regionKey;
  });

  const byDay = new Map();
  filtered.forEach((item) => {
    const day = item.dayOfWeek;
    if (!day) return;
    const slotId = codeById[item.slotId] || null;
    if (!slotId) return;
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push({
      slotId,
      slot: MEAL_SLOT_TYPES.find((t) => t.id === slotId)?.label || slotId,
      item: item.name || '—',
      portion: item.portion || '',
      calories: item.calories || '',
    });
  });

  const rows = Array.from(byDay.entries()).map(([day, meals]) => ({
    day,
    meals: sortMealsBySlotOrder(meals),
  }));
  return expandToFullWeek(sortWeekdays(rows));
}

export function regionKeysFromIndicativeItems(items) {
  const keys = new Set();
  (items || []).forEach((item) => {
    if (item?.regionKey) keys.add(item.regionKey);
  });
  return Array.from(keys);
}

/**
 * Build SpecialtyPlanDetailModal-ready plan from masters + indicative items.
 */
export function buildPlanFromApi({
  specialty,
  slots,
  prices,
  indicativeItems = [],
  overlay = {},
}) {
  if (!specialty) return null;
  const pricing = groupPricesForSpecialty(prices, specialty.id);
  const code = specialty.code;
  const supportsRegion = Boolean(specialty.supportsRegion);
  const regionKeys = supportsRegion
    ? (regionKeysFromIndicativeItems(indicativeItems).length
      ? regionKeysFromIndicativeItems(indicativeItems)
      : ['south', 'north'])
    : [];

  let regionalMenus = null;
  let weeklyMenu = [];

  if (supportsRegion && regionKeys.length) {
    regionalMenus = {};
    regionKeys.forEach((key) => {
      regionalMenus[key] = {
        label: REGION_LABELS[key] || `${key} Meal`,
        weeklyMenu: indicativeItemsToWeeklyMenu(indicativeItems, slots, key),
      };
    });
    weeklyMenu = regionalMenus[regionKeys[0]]?.weeklyMenu || [];
  } else {
    weeklyMenu = indicativeItemsToWeeklyMenu(indicativeItems, slots, null);
  }

  const caption = specialty.caption || overlay.tagline || '';
  const iconCode = specialty.iconCode || overlay.iconCode || code;
  const logoUrl = specialty.logoUrl || overlay.logoUrl || null;

  return {
    id: code,
    specialtyId: specialty.id,
    name: specialty.name,
    tagline: caption,
    caption,
    iconCode,
    logoUrl,
    accent: overlay.accent || '#e05a00',
    icon: iconCode,
    iconComponent: overlay.iconComponent || null,
    organicOnly: Boolean(overlay.organicOnly),
    supportsRegion,
    pricing,
    fromPrice: pickFromPriceRow(pricing),
    weeklyMenu,
    regionalMenus,
    slots: (slots || []).filter((s) => s.status !== 'hidden'),
    source: 'api',
    fullDayMealDiscountPercent: Number.isFinite(Number(specialty.fullDayMealDiscountPercent))
      ? Number(specialty.fullDayMealDiscountPercent)
      : 20,
  };
}

export function buildCatalogPlansFromApi({ specialties, slots, prices, overlaysByCode = {} }) {
  const active = (specialties || [])
    .filter((s) => s.status !== 'hidden')
    .slice()
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return active.map((specialty) =>
    buildPlanFromApi({
      specialty,
      slots,
      prices,
      indicativeItems: [],
      overlay: overlaysByCode[specialty.code] || SPECIALTY_UI_OVERLAYS[specialty.code] || {},
    }),
  );
}

/** Attach menus loaded for a specialty (and optionally merge into existing plan). */
export function withIndicativeMenus(plan, indicativeItems, slots) {
  if (!plan) return null;
  return buildPlanFromApi({
    specialty: {
      id: plan.specialtyId,
      code: plan.id,
      name: plan.name,
      supportsRegion: plan.supportsRegion,
      status: 'active',
      sortOrder: 0,
    },
    slots,
    prices: flattenPricingForRebuild(plan),
    indicativeItems,
    overlay: {
      tagline: plan.tagline,
      accent: plan.accent,
      iconComponent: plan.iconComponent,
      organicOnly: plan.organicOnly,
    },
  });
}

/** Rebuild API-like price rows from UI pricing so buildPlanFromApi can re-group. */
function flattenPricingForRebuild(plan) {
  const rows = [];
  (plan.pricing || []).forEach((row, index) => {
    const slotId = row.slotId
      ?? (SINGLE_SLOT_BUNDLES[row.bundle] ? undefined : null);
    if (row.perDayNonOrganic != null) {
      rows.push({
        id: `n-${index}`,
        specialtyId: plan.specialtyId,
        slotId: slotId ?? null,
        bundleKey: row.bundle,
        isOrganic: false,
        perDayPrice: row.perDayNonOrganic,
        exclGst: row.exclGstNonOrganic,
        status: 'active',
      });
    }
    if (row.perDayOrganic != null) {
      rows.push({
        id: `o-${index}`,
        specialtyId: plan.specialtyId,
        slotId: slotId ?? null,
        bundleKey: row.bundle,
        isOrganic: true,
        perDayPrice: row.perDayOrganic,
        exclGst: row.exclGstOrganic,
        status: 'active',
      });
    }
  });
  return rows;
}
